import {Coupon} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {useParams} from '@aboutyou/router';
import {useEffect} from 'react';
import {getCouponPromotionId} from '../../../../../features/Coupons/getCouponPromotionId';
import {isAppOnlyCoupon} from '../../../../../features/Coupons/isAppOnlyCoupon';
import {useActivatedCouponState} from '../../../../../features/Coupons/useActivatedCouponState';
import {useCouponPromotionIdFromQueryParams} from '../../../../../features/Coupons/useCouponPromotionIdFromQueryParams';

// activate a coupon from query parameter or page parameter
export const useActivateCouponFromUrl = ({
  productId,
  coupons,
}: {
  productId: number;
  coupons: Coupon[];
}) => {
  const {couponPromotionId, setActivatedCoupon} =
    useActivatedCouponState(productId);
  const couponPromotionIdFromQueryParams =
    useCouponPromotionIdFromQueryParams();
  const {promotionId: couponPromotionIdFromPathParams} = useParams();

  useEffect(() => {
    if (!couponPromotionIdFromPathParams) {
      return;
    }

    // if the couponPromotionId is already set, we should not automatically apply a coupon from the path params
    // this is to prevent overriding the coupon that was already activated by the user
    if (couponPromotionId) {
      return;
    }

    // couponPromotionId will be null only if the user actively deselected a coupon
    // in that case we do not want to fallback to activate a coupon from the URL
    if (couponPromotionId === null) {
      return;
    }

    const coupon = coupons.find(
      coupon =>
        getCouponPromotionId(coupon) === couponPromotionIdFromPathParams,
    );

    if (!coupon) {
      return;
    }
    if (isAppOnlyCoupon(coupon)) {
      // If the coupon is app-only, we do not activate it from the URL
      return;
    }

    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setActivatedCoupon(coupon);
  }, [coupons]);

  useEffect(() => {
    if (!couponPromotionIdFromQueryParams) {
      return;
    }

    const coupon = coupons.find(
      coupon =>
        getCouponPromotionId(coupon) === couponPromotionIdFromQueryParams,
    );

    if (
      coupon &&
      couponPromotionId !== getCouponPromotionId(coupon) &&
      !isAppOnlyCoupon(coupon)
    ) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setActivatedCoupon(coupon);
    }
  }, [coupons]);
};
